/**
 * Per-run state. Module-level singleton; the SaveManager (saveManager.ts)
 * round-trips it through localStorage as a versioned snapshot.
 *
 * The serializable shape (RunStateSnapshot) is the contract with the save
 * file. Adding a field is a v→v+1 schema bump in saveManager's migrations
 * registry. Removing or repurposing a field requires a migration.
 */

import { computeMaxHp } from '../data/baseHp';
import { expForLevel, levelForExp } from '../data/expCurve';
import { getMovesAtLevel } from '../data/levelUpMoves';
import { randomUUID } from './uuid';
import { INITIAL_HIDDEN_FLAGS } from '../scripts/flagRegistry';
import type { Nature } from '../data/natures';
import type { BaseStats } from '../data/computeStat';
import type { DraftOffer } from './draftRoll';
import type { PartyMemberStatus } from '../battle/statusVisuals';

export type { PartyMemberStatus };

export type IvSet = {
  hp: number;
  atk: number;
  def: number;
  spa: number;
  spd: number;
  spe: number;
};

/** Default IV set used by addToParty when no offer-time roll has plumbed
 *  through yet (Step 2 placeholder; Step 3 supplies real values). All
 *  zeroes is the "we don't know yet" sentinel — the Pokédex full sheet
 *  can render placeholder rows until the real IVs flow in. */
const ZERO_IVS: IvSet = { hp: 0, atk: 0, def: 0, spa: 0, spd: 0, spe: 0 };

export type Gender = 'male' | 'female' | 'genderless';

/** Where + when a Pokémon was caught — captured at draft-offer time from
 *  the player's current overworld position. tileX/tileY are stored but
 *  not yet displayed (TODOs #25 will surface them in the tile memorial).
 *  v9. */
export interface CaughtAt {
  mapName: string;
  tileX: number;
  tileY: number;
  when: number;
}

/** Sentinel caughtAt used by addToParty when the caller doesn't supply
 *  one (test ergonomics + cross-development states where the field was
 *  added mid-PR), and by restore() defensively when a snapshot lacks the
 *  field. Production paths under v9 always plumb a real caughtAt through
 *  DraftScene.commit. Empty mapName signals "unknown" to any consumer
 *  that renders Caught: <map>. v9. */
const UNKNOWN_CAUGHT_AT: CaughtAt = {
  mapName: '',
  tileX: 0,
  tileY: 0,
  when: 0,
};

export interface PartyMember {
  /** Per-instance identity. uuid v4 via browser-native crypto.randomUUID().
   *  Generated at draft-offer time (Step 3) — for Step 1, addToParty
   *  generates a placeholder UUID when the caller doesn't pass one.
   *  Stable across saves, sleeps, and switches; the load-bearing key for
   *  BattleScene.hpById + faintedIds (replaces the species-keyed HP map
   *  that collided when two same-species Pokémon coexisted in a party).
   *  v7 save schema. */
  id: string;
  /** @pkmn species id (lowercase, e.g. 'bulbasaur'). */
  species: string;
  level: number;
  /** Resolved at draft-pick time via getMovesAtLevel; updated by addExp
   *  when level-up resolves new moves. Up to 4 entries; new moves past
   *  the cap are auto-skipped in v0.1 (no replace-which-move modal). */
  moves?: string[];
  /** Current/max HP. Persists across battles + within battles (Phase 12 M1).
   *  Defaults to {current=max, max=computeMaxHp(species, level)} for fresh
   *  drafts. The Pokémon Center heal sets current=max. Faint events leave
   *  the slot with current=0 briefly before runState splices it on
   *  battleEnd, so a non-fainted member is the invariant for surviving
   *  party slots. */
  hp: { current: number; max: number };
  /** Total EXP earned. Cumulative across the run; never decreases.
   *  Defaults to expForLevel(level) for fresh drafts so level + exp stay
   *  consistent. addExp() handles cross-threshold level-ups (chain ups
   *  on big trainer wins). v5 save schema. */
  exp: number;
  /** Cartridge nature. Determines the 1.1×/0.9× stat modifier pair (or
   *  neutral). Rolled once at draft-offer time and never updates. Step 2
   *  introduces the field with a 'hardy' (neutral) placeholder; Step 3
   *  plumbs the real offer-time roll through. v7. */
  nature: Nature;
  /** Cartridge IVs (0-31 each, six stats). Rolled at draft-offer time;
   *  immutable across the Pokémon's lifetime. Step 2 placeholder is all
   *  zeroes until Step 3 wires offer-time rolls. v7. */
  ivs: IvSet;
  /** @pkmn-style normalized ability id (lowercase, no spaces — e.g.
   *  'static', 'overgrow'). Rolled at draft-offer time from species
   *  abilities. Step 2 placeholder is '' (unknown) until Step 3 wires
   *  the rollAbility call. v7. */
  ability: string;
  /** Cartridge-faithful gender, resolved at draft-offer time via
   *  rollGender (species.gender + species.genderRatio from @pkmn/dex).
   *  Immutable across the Pokémon's lifetime. Memoir thread — surfaces
   *  on the draft card + Pokédex + careerStore eulogy. v9. */
  gender: Gender;
  /** Where + when the offer was rolled. mapName + tile coords + epoch
   *  ms timestamp from the player's current overworld position. Display
   *  as "Caught: {mapDisplayName(mapName)}" on the draft card BACK +
   *  Pokédex + Career. Tile coords reserved for TODOs #25 tile memorial.
   *  v9. */
  caughtAt: CaughtAt;
  /** PR 3 EXPANSION 5: persistent status condition. Mirrors sim's
   *  internal `pokemon.status` string verbatim so the cross-battle
   *  round-trip is byte-stable.
   *
   *  Write moment: BattleScene.flushBattleStateToRunState reads each
   *  member's sim-side status at battleEnd + writes here.
   *  Read moment: runInteractiveBattle.startInteractiveBattle injects
   *  the stored value back into sim's internal state at battle entry
   *  (symmetric to existing HP injection at line ~85).
   *  Faint clears: handled by the existing splice path (the slot is
   *  removed from runState; no separate status reset needed).
   *  Center heal: runState.healAll sets status = null alongside HP.
   *
   *  v10. Save migration v9→v10 is destructive per CLAUDE.md policy. */
  status: PartyMemberStatus;
}

/** Returned from addExp so BattleScene can log each step of a multi-level
 *  chain. Carries enough info for "X grew to Lv N!" + "X learned MOVE!"
 *  log lines without the caller re-walking the party.
 *
 *  PR 1 (forget-which-move): a level that wants to teach a new move
 *  while all 4 slots are full sets `pendingMove` + `requiresChoice`.
 *  The caller (BattleScene.battleEnd's EXP loop) awaits the
 *  LearnedMovesModal, then dispatches `applyMoveChoice` to commit
 *  (or skip) the substitution. */
export interface LevelUpEvent {
  /** Party index of the leveler (post-modification — they're still alive). */
  partyIndex: number;
  /** Species at level-up time (defensive: might differ from index later). */
  species: string;
  /** New level reached (one event per level if chained — L5→L7 = 2 events). */
  level: number;
  /** Names of moves newly learned at this level (lowercase ids; auto-added
   *  to empty slots; empty array if none new or all 4 slots were already
   *  full). */
  newMoves: string[];
  /** Move that wanted to be learned at this level but all 4 slots were
   *  full. Caller awaits LearnedMovesModal then commits via
   *  applyMoveChoice. Undefined when no overflow. v0.1 edge case: if
   *  two moves would be learned at the SAME level AND both overflow,
   *  only the first is surfaced as `pendingMove`; subsequent overflows
   *  at the same level are dropped (cartridge fires sequential prompts
   *  for each — TODOs follow-up if a friend ever hits the case). */
  pendingMove?: string;
  /** True iff `pendingMove` is set — caller MUST surface the
   *  forget-which-move ceremony before advancing the EXP loop. The
   *  flag is redundant with `pendingMove !== undefined`; kept as a
   *  discoverable contract in the consumer. */
  requiresChoice?: boolean;
}

/** Player's overworld position — used to resume the run on page reload
 *  at the same map / tile / facing. Null until WorldScene first writes
 *  a value (legacy v5 saves migrate to null and boot to the bedroom). */
export interface PlayerPosition {
  /** Converter map basename (e.g., 'ViridianForest') — what loadMap takes. */
  mapName: string;
  tileX: number;
  tileY: number;
  facing: 'up' | 'down' | 'left' | 'right';
}

/** Pending draft snapshot — set when WorldScene rolls offers for a zone
 *  before launching DraftScene; cleared on pick or on Reset Run. Survives
 *  page reload so the player resumes with the SAME offers they were
 *  weighing (no re-roll on continue). v7. */
export interface PendingDraft {
  zoneId: string;
  offers: DraftOffer[];
}

/** Frozen post-pick snapshot of a drafted Pokémon. Written once at
 *  DraftScene.commit() time; NEVER updated thereafter — not on faint,
 *  not on level-up, not on move learn. Death info is read at render
 *  time via JOIN with careerStore by id (Step 7). The snapshot lives
 *  in pokedex.drafted for the run; the live PartyMember in runState.party
 *  is the mid-run source of truth, while this snapshot is the
 *  "captured-at-draft" record the Pokédex displays.
 *
 *  Shape denormalizes display fields (displayName/dexId/types) from
 *  DraftOffer so the Pokédex can render without a dex lookup at row-
 *  open time. v7. */
export interface PartyMemberSnapshot {
  id: string;
  species: string;
  displayName: string;
  dexId: number;
  types: string[];
  level: number;
  moves: string[];
  /** Title-case display moves aligned with moves[] by index. */
  movesDisplay: string[];
  nature: Nature;
  ivs: IvSet;
  ability: string;
  /** Display name + short description from @pkmn/dex (denormalized so
   *  PokedexScene rows can render without an async dex lookup). */
  abilityName: string;
  abilityDesc: string;
  /** Base stats for the in-battle stat computation in PokemonFullSheet. */
  baseStats: BaseStats;
  eggPullMove?: string;
  isRarePull: boolean;
  /** Frozen-at-pick gender + caughtAt — copied from DraftOffer at
   *  DraftScene.commit() time. The snapshot is immutable, so these are
   *  the canonical record for the Pokédex's THIS RUN tab. v9. */
  gender: Gender;
  caughtAt: CaughtAt;
}

/** The two offers per modal that the player didn't pick. Written at
 *  DraftScene.commit() alongside the snapshot. Carries the full offer
 *  sheet so the Skipped tab in the Pokédex can render "I skipped a
 *  Timid 31-Sp.A Pikachu" as a recoverable memory. v7. */
export interface SkippedEntry {
  offer: DraftOffer;
  /** Species of the offer that was picked instead (shared across both
   *  skipped entries from the same modal). */
  passedForSpecies: string;
  zoneId: string;
  /** Unix ms — when the player committed the pick. */
  pickedAt: number;
}

/** Per-species wild-encounter tally. Written by WorldScene when a
 *  grass-step encounter triggers (the player has visually seen the
 *  species by that point — v0.1 has no flee path). Count + level band
 *  only; species metadata derives from @pkmn/dex at render time. v7. */
export interface SeenWildEntry {
  count: number;
  minLevel: number;
  maxLevel: number;
}

/** Per-run Pokédex state. Three sub-collections, all clear on Reset Run.
 *  Career-graveyard entries (across-run records) live separately in
 *  careerStore on the poke:career localStorage key (Step 7). v7. */
export interface PokedexThisRun {
  drafted: PartyMemberSnapshot[];
  skipped: SkippedEntry[];
  seenWild: Record<string, SeenWildEntry>;
}

/** The shape persisted to localStorage. Bump SAVE_VERSION + add a migration
 *  in saveManager.ts when this evolves. */

export interface RunStateSnapshot {
  party: PartyMember[];
  /** Map ids whose route draft has fired. Stored as an array so JSON works;
   *  the runtime keeps it as a Set. */
  routesDrafted: string[];
  /** Trainer scriptRefs (e.g., 'PewterCity_Gym_EventScript_Brock') the player
   *  has already defeated this run. Trainers in this set don't re-trigger LOS
   *  battles on subsequent passes. Phase 12 M4. */
  defeatedTrainers: string[];
  /** Resume-on-reload position. Null = boot to the default bedroom. */
  position: PlayerPosition | null;
  /** Species the player has DRAFTED this run (not skipped, not seen wild).
   *  Used by rollOffers as the excluded-species filter so the same species
   *  doesn't appear in two consecutive zone drafts. v7. Stored as an array
   *  so JSON works; the runtime keeps it as a Set. */
  draftedSpeciesThisRun: string[];
  /** In-flight draft modal — set when WorldScene rolls offers + launches
   *  DraftScene, cleared by DraftScene on pick OR by runState.reset().
   *  Persists across reload so the player resumes mid-draft with the SAME
   *  offers they were weighing (no re-roll). v7. */
  pendingDraft: PendingDraft | null;
  /** Per-run Pokédex — drafted snapshots, skipped offers, wild encounter
   *  tally. Powers PokedexScene's THIS RUN tab. Career-graveyard entries
   *  live separately at poke:career localStorage. v7. */
  pokedex: PokedexThisRun;
  /** Scripted-handler flag set — bits like FLAG_OAK_INTRO_DONE,
   *  FLAG_STARTER_PICKED, FLAG_HIDE_OAK_IN_PALLET_TOWN. Per-run; Reset Run
   *  wipes. Stored as an array on disk; runtime is a Set. v11. */
  flags: string[];
  /** Scripted-handler variable map — short string→string for cartridge
   *  varN slots (rival starter pick, dialogue counters, etc.). Per-run.
   *  Stored as JSON-keyed object. v11. */
  vars: Record<string, string>;
  /** Bag inventory — itemId → count (only ids in the ITEMS registry).
   *  Per-run; Reset Run wipes. Zero-count entries are removed, never
   *  stored. v14. */
  bag: Record<string, number>;
  /** Region name → count of entries into that region this
   *  run. The roamer population for a visit derives deterministically
   *  from (master seed, region, visit count) — so a mid-visit reload
   *  re-derives the SAME population (no reload-to-reroll scumming)
   *  while roamer positions stay derived-not-persisted, per the
   *  follower's rule. Incremented on genuine region entry only. Always
   *  {} in classic mode. v16. */
  spawnVisits: Record<string, number>;
  /** Roam mode: region name → spawn ordinals (indices into the visit's
   *  derived cast) already consumed by battles THIS visit. Without
   *  this, a reload re-derives the full cast and consumed roamers
   *  resurrect — an EXP refill the respawn policy ("refill only on
   *  genuine re-entry") forbids. Cleared when the region's visit
   *  bumps. Always {} in classic mode. v16 (adversarial review
   *  2026-08-02). */
  spawnConsumed: Record<string, number[]>;
}

function freshPokedex(): PokedexThisRun {
  return { drafted: [], skipped: [], seenWild: {} };
}

class RunStateStore {
  private _party: PartyMember[] = [];
  private _routesDrafted: Set<string> = new Set();
  private _defeatedTrainers: Set<string> = new Set();
  private _position: PlayerPosition | null = null;
  private _draftedSpeciesThisRun: Set<string> = new Set();
  private _pendingDraft: PendingDraft | null = null;
  private _pokedex: PokedexThisRun = freshPokedex();
  // PR 12: singleton is born in a valid run-start state with INITIAL_HIDDEN_
  // FLAGS seeded. reset() re-seeds; restore() overrides from the snapshot
  // (which itself carries them from the prior run's reset()).
  private _flags: Set<string> = new Set(INITIAL_HIDDEN_FLAGS);
  private _vars: Record<string, string> = {};
  private _bag: Record<string, number> = {};
  private _spawnVisits: Record<string, number> = {};
  private _spawnConsumed: Record<string, number[]> = {};

  // ─── Named subscribe methods (NOT a generic event bus) ───────────
  //
  // Eng review outside voice: a generic event bus is a real architectural
  // primitive this codebase doesn't otherwise have. Inlining two named
  // subscribe methods avoids smuggling that primitive in as plumbing
  // for one consumer (PartyOrbs). When a 4th+ named subscriber lands,
  // re-evaluate then.

  private partyChangedListeners: Array<() => void> = [];
  private membersRemovedListeners: Array<(ids: string[]) => void> = [];
  private partyChangedScheduled = false;

  /** Subscribe to broad "party state changed" notifications. Fires
   *  after any HP / level / move / order mutation. Coalesces multiple
   *  synchronous emits into one microtask flush so subscribers don't
   *  see render storms (e.g., BattleScene.battleEnd's 6 sequential
   *  setMemberHpById calls collapse to ONE PartyOrbs refresh). */
  onPartyChanged(cb: () => void): () => void {
    this.partyChangedListeners.push(cb);
    return () => {
      this.partyChangedListeners = this.partyChangedListeners.filter((c) => c !== cb);
    };
  }

  /** Subscribe to "these party member ids just got removed". Fires
   *  once per removeWhere / removeFromParty / reset call with batched
   *  ids. NOT coalesced — each removal is its own event because the
   *  caller already batches (BattleScene.battleEnd calls removeWhere
   *  ONCE with the full faintedIds set). */
  onMembersRemoved(cb: (ids: string[]) => void): () => void {
    this.membersRemovedListeners.push(cb);
    return () => {
      this.membersRemovedListeners = this.membersRemovedListeners.filter((c) => c !== cb);
    };
  }

  private notifyPartyChanged(): void {
    if (this.partyChangedScheduled) return;
    this.partyChangedScheduled = true;
    queueMicrotask(() => {
      this.partyChangedScheduled = false;
      // Copy the listeners array so a subscriber that disposes itself
      // mid-iteration doesn't skip the next cb in the loop.
      for (const cb of this.partyChangedListeners.slice()) cb();
    });
  }

  private notifyMembersRemoved(ids: string[]): void {
    if (ids.length === 0) return;
    for (const cb of this.membersRemovedListeners.slice()) cb([...ids]);
  }

  getParty(): readonly PartyMember[] {
    return this._party;
  }

  /** Add a member with HP + EXP defaulted (full HP, exp = expForLevel(level)
   *  so level + exp stay consistent). Drafts go through here, so newly-
   *  picked Pokémon enter at full HP + the minimum EXP for their level.
   *
   *  `id`, `nature`, `ivs`, and `ability` are optional for Steps 1 + 2 —
   *  addToParty supplies placeholders when the caller doesn't pass them.
   *  Step 3 wires DraftOffer.{id, nature, ivs, ability} through so the
   *  same instance identity + rolled stats transfer from the offer modal
   *  to the party slot (load-bearing for the Pokédex's Skipped tab regret
   *  mechanic). */
  addToParty(
    member: Omit<
      PartyMember,
      'hp' | 'exp' | 'id' | 'nature' | 'ivs' | 'ability' | 'gender' | 'caughtAt' | 'status'
    > & {
      id?: string;
      hp?: PartyMember['hp'];
      exp?: number;
      nature?: Nature;
      ivs?: IvSet;
      ability?: string;
      gender?: Gender;
      caughtAt?: CaughtAt;
      status?: PartyMemberStatus;
    },
  ): void {
    const id = member.id ?? randomUUID();
    // IVs resolve BEFORE hp — stats-integrity: max HP derives from the
    // member's actual HP IV (placeholder members get placeholder-
    // consistent HP too; the old build computed 31-IV maxes for
    // ZERO_IVS placeholders, which was silently inconsistent).
    const ivs: IvSet = member.ivs ?? { ...ZERO_IVS };
    const hp =
      member.hp ?? {
        current: computeMaxHp(member.species, member.level, ivs.hp),
        max: computeMaxHp(member.species, member.level, ivs.hp),
      };
    const exp = member.exp ?? expForLevel(member.level);
    const nature: Nature = member.nature ?? 'hardy';
    const ability: string = member.ability ?? '';
    const gender: Gender = member.gender ?? 'genderless';
    const caughtAt: CaughtAt = member.caughtAt
      ? { ...member.caughtAt }
      : { ...UNKNOWN_CAUGHT_AT };
    const status: PartyMemberStatus = member.status ?? null;
    this._party.push({ ...member, id, hp, exp, nature, ivs, ability, gender, caughtAt, status });
    this.notifyPartyChanged();
  }

  removeFromParty(index: number): void {
    if (index < 0 || index >= this._party.length) return;
    const [removed] = this._party.splice(index, 1);
    if (removed) {
      this.notifyMembersRemoved([removed.id]);
      // The party array changed — onPartyChanged subscribers (e.g.
      // the follower's lead-species tracker in WorldScene) must see
      // the new lead too. Matches the restore() / reset() pattern.
      this.notifyPartyChanged();
    }
  }

  /**
   * Move a party member from `fromIdx` to `toIdx`. INSERT semantics, NOT
   * swap: source removed, the others shift to close the gap, source
   * inserted at the target index.
   *
   *   [A,B,C,D,E] reorderParty(0, 3) → [B,C,D,A,E]   (insert)
   *                                  NOT [D,B,C,A,E] (swap — wrong)
   *
   * Bounds-clamped; no-op if from === to OR either index out of range.
   * Does NOT trigger save — caller responsibility (matches the existing
   * convention for addToParty / removeFromParty / addExp).
   *
   * Two call sites in the orb-chrome PR: (a) the drag-handler in
   * PartyOrbs after a successful drop; (b) PokemonFullSheet's "Set as
   * lead" CTA. Both call saveManager.save() after.
   */
  reorderParty(fromIdx: number, toIdx: number): void {
    if (fromIdx === toIdx) return;
    if (fromIdx < 0 || fromIdx >= this._party.length) return;
    if (toIdx < 0 || toIdx >= this._party.length) return;
    const [moved] = this._party.splice(fromIdx, 1);
    if (!moved) return;
    this._party.splice(toIdx, 0, moved);
    this.notifyPartyChanged();
  }

  /** Set the current HP for a member by party index. Legacy index-based
   *  API kept for non-battle callers (Pokémon Center heal-all iterates by
   *  reference, no lookup needed). Battle code uses setMemberHpById —
   *  see method below for the rationale. Clamped to [0, max] to defend
   *  against off-by-one in the source. */
  setMemberHp(index: number, current: number): void {
    const m = this._party[index];
    if (!m) return;
    m.hp.current = Math.max(0, Math.min(current, m.hp.max));
    this.notifyPartyChanged();
  }

  /** Set the current HP for a member by instance id. This is the canonical
   *  battle-end flush path now that BattleScene tracks HP by id instead of
   *  by species (the species-keyed map collided when two same-species
   *  Pokémon coexisted; force-switching in slot 2 first and fainting it
   *  before slot 1 left slot 1 reading the dead one's HP on re-entry).
   *  Returns silently when no member matches — defensive against a stale
   *  flush after the slot was already spliced. v7. */
  setMemberHpById(id: string, current: number): void {
    const m = this._party.find((p) => p.id === id);
    if (!m) return;
    m.hp.current = Math.max(0, Math.min(current, m.hp.max));
    this.notifyPartyChanged();
  }

  /** Set the persistent status condition for a member by instance id.
   *  Called by BattleScene.flushBattleStateToRunState on battleEnd /
   *  runFromBattle for every non-fainted member (the active + the
   *  reserves), reading the value from sim's internal pokemon.status
   *  string. Returns silently when no member matches — defensive
   *  against a stale flush after the slot was already spliced. v10. */
  setMemberStatusById(id: string, status: PartyMemberStatus): void {
    const m = this._party.find((p) => p.id === id);
    if (!m) return;
    m.status = status;
    this.notifyPartyChanged();
  }

  /** Pokémon Center heal: restore every party member to full HP AND
   *  clear every persistent status condition. The heal dialogue text
   *  in WorldScene reflects the dual restore. v10. */
  healAll(): void {
    for (const m of this._party) {
      m.hp.current = m.hp.max;
      m.status = null;
    }
    this.notifyPartyChanged();
  }

  /**
   * Award EXP to a party slot and resolve any level-ups that crossed.
   * Async because move-learning uses the lazy-loaded gen-3 dex.
   *
   * Per-level beats:
   *   1. Increment level
   *   2. Recompute hp.max from new level (computeMaxHp); set hp.current
   *      = hp.max (full heal — pragmatic for a permadeath roguelike;
   *      cartridge keeps the HP delta but our v0.1 trades that for a
   *      satisfying "you grew!" beat that also clears damage).
   *   3. Resolve the new level's full moveset; auto-add any move not
   *      already known into an empty slot. If all 4 slots are taken,
   *      log it but skip — v0.1 doesn't have a replace-which-move modal.
   *
   * Returns one LevelUpEvent per level reached (multi-level chains
   * possible from big trainer wins). Empty array means no level-up.
   * BattleScene logs each event after this resolves.
   */
  async addExp(index: number, amount: number): Promise<LevelUpEvent[]> {
    const m = this._party[index];
    if (!m || amount <= 0) return [];
    m.exp += amount;
    const oldLevel = m.level;
    const newLevel = levelForExp(m.exp, oldLevel);
    if (newLevel === oldLevel) return [];

    const events: LevelUpEvent[] = [];
    for (let lvl = oldLevel + 1; lvl <= newLevel; lvl++) {
      m.level = lvl;
      // Cartridge convention: current HP increases by exactly the delta
      // between the old max and the new max — i.e., the wound stays the
      // same absolute size, the Pokémon doesn't free-heal. A 5/20 mon
      // leveling up to max 22 becomes 7/22, not 22/22. v0.1 used to
      // full-heal here (it was an early shortcut that became user-
      // visible once the cathedral combat work surfaced level-ups
      // through the cinematic + battle log); user reported the unexpected
      // free heal post-cathedral, so dialing it back to the cartridge
      // rule.
      const oldMax = m.hp.max;
      const newMax = computeMaxHp(m.species, lvl, m.ivs.hp);
      const delta = Math.max(0, newMax - oldMax);
      m.hp.max = newMax;
      m.hp.current = Math.min(newMax, m.hp.current + delta);

      // Move-learning: ask the dex what this species knows by `lvl`. The
      // dex helper returns the FULL moveset up to `lvl`, not just the
      // delta — so we diff against current moves. New moves go into
      // empty slots first; once 4 are taken, PR 1 surfaces the first
      // overflow on the LevelUpEvent's `pendingMove` for the caller to
      // resolve via LearnedMovesModal + applyMoveChoice.
      const knownAtLevel = await getMovesAtLevel(m.species, lvl);
      const known = new Set(m.moves ?? []);
      const newMoves: string[] = [];
      let pendingMove: string | undefined;
      for (const mv of knownAtLevel) {
        if (known.has(mv)) continue;
        if ((m.moves ?? []).length >= 4) {
          // Slot full — first overflow at this level surfaces as the
          // pendingMove. The caller's LearnedMovesModal flow may
          // commit (replace) or skip. We do NOT mutate moves[] here
          // for overflow cases — that's applyMoveChoice's job.
          if (pendingMove === undefined) pendingMove = mv;
          continue;
        }
        m.moves = [...(m.moves ?? []), mv];
        newMoves.push(mv);
      }

      events.push({
        partyIndex: index,
        species: m.species,
        level: lvl,
        newMoves,
        ...(pendingMove !== undefined
          ? { pendingMove, requiresChoice: true }
          : {}),
      });
    }
    if (events.length > 0) this.notifyPartyChanged();
    return events;
  }

  /**
   * Apply the player's forget-which-move decision from LearnedMovesModal.
   * Called by BattleScene.battleEnd's EXP loop after the modal resolves.
   *
   *   forgetIndex = 0..3 → replace moves[forgetIndex] with newMove.
   *   forgetIndex = null  → "Don't learn" — no mutation.
   *
   * No-op when partyIndex is out of bounds, when the slot to replace
   * is out of bounds, or when the member has fewer than 4 moves (the
   * caller only invokes this on slot-full overflows, so a < 4 case
   * means the party state shifted between addExp + the modal resolve
   * — defensive bail rather than appending past slot 4).
   *
   * Idempotent in the sense that calling with forgetIndex=null is
   * always safe. notifyPartyChanged fires on commit so PartyOrbs +
   * any future "moves" badge re-renders.
   */
  applyMoveChoice(
    partyIndex: number,
    forgetIndex: number | null,
    newMove: string,
  ): void {
    if (forgetIndex === null) return;
    const m = this._party[partyIndex];
    if (!m) return;
    const moves = m.moves ?? [];
    if (forgetIndex < 0 || forgetIndex >= 4) return;
    if (moves.length < 4) return;
    const next = moves.slice();
    next[forgetIndex] = newMove;
    m.moves = next;
    this.notifyPartyChanged();
  }

  /** Permadeath helper: drop every member matching the predicate. Returns
   *  the count removed. */
  removeWhere(pred: (m: PartyMember) => boolean): number {
    const removedIds: string[] = [];
    const keep: PartyMember[] = [];
    for (const m of this._party) {
      if (pred(m)) removedIds.push(m.id);
      else keep.push(m);
    }
    this._party = keep;
    if (removedIds.length > 0) {
      this.notifyMembersRemoved(removedIds);
      // The party array changed (and the lead may have changed if
      // index 0 was among the removed) — onPartyChanged subscribers
      // (e.g. the follower's lead-species tracker in WorldScene)
      // must see the new lead too. Matches the restore() / reset()
      // pattern. Without this, BattleScene's permadeath splice would
      // leave the dead Pokémon walking behind the player back in the
      // overworld until the next mutation (HP / level / draft) fired
      // onPartyChanged for an unrelated reason.
      this.notifyPartyChanged();
    }
    return removedIds.length;
  }

  hasPartyMember(): boolean {
    return this._party.length > 0;
  }

  /**
   * Convenience for the "first encounter on Route 101 → starter draft" flow
   * that Phase 9 M2 ships. Per-route draft tracking (Phase 9 M3) uses the
   * separate hasDrafted/markDrafted pair below.
   */
  hasStarter(): boolean {
    return this._party.length > 0;
  }

  hasDrafted(mapId: string): boolean {
    return this._routesDrafted.has(mapId);
  }

  markDrafted(mapId: string): void {
    this._routesDrafted.add(mapId);
  }

  hasDefeatedTrainer(scriptRef: string): boolean {
    return this._defeatedTrainers.has(scriptRef);
  }

  markDefeatedTrainer(scriptRef: string): void {
    this._defeatedTrainers.add(scriptRef);
  }

  // ─── Scripted-handler flag / var store (MVP H1) ───────────────────
  //
  // Per-run state for cartridge-faithful scripted moments (Oak intercept,
  // rival lab cinematic, hidden-NPC FLAG_HIDE_* visibility filter, etc.).
  // Eng review decision 12: this is NET-NEW infrastructure, not a
  // generalization of defeatedTrainers (which is a write-once defeat ledger
  // with different semantics). Decision 20: setFlag does NOT autosave —
  // handlers explicitly `await ctx.save()` at handler-end, matching the
  // existing M3 heal pattern. Reset Run wipes flags + vars (reset()
  // below); career graveyard on poke:career is untouched.

  hasFlag(flag: string): boolean {
    return this._flags.has(flag);
  }

  setFlag(flag: string): void {
    this._flags.add(flag);
  }

  clearFlag(flag: string): void {
    this._flags.delete(flag);
  }

  getVar(key: string): string | undefined {
    return this._vars[key];
  }

  setVar(key: string, value: string): void {
    this._vars[key] = value;
  }

  clearVar(key: string): void {
    delete this._vars[key];
  }

  // ─── Bag inventory (G2, v14). Same per-run lifecycle as flags/vars:
  // serialize/restore round-trips, Reset Run wipes. No subscriber —
  // BagScene reads on open; battle-bag reads per turn (G10/G11).

  // ─── Spawn visits: the deterministic key for each region's cast.

  /** Record a genuine entry into `region` and return the new visit
   *  count. The count keys the deterministic roamer-population
   *  derivation for this visit — see seedStore.spawnRngFor. A new
   *  visit means a fresh cast, so the region's consumed ordinals
   *  reset with it. */
  bumpSpawnVisit(region: string): number {
    const next = (this._spawnVisits[region] ?? 0) + 1;
    this._spawnVisits[region] = next;
    delete this._spawnConsumed[region];
    return next;
  }

  /** Current visit count for `region` (0 = never entered this run). */
  getSpawnVisit(region: string): number {
    return this._spawnVisits[region] ?? 0;
  }

  /** Mark spawn ordinal `index` of `region`'s current-visit cast as
   *  consumed (battle resolved). Idempotent. */
  markSpawnConsumed(region: string, index: number): void {
    const list = this._spawnConsumed[region] ?? (this._spawnConsumed[region] = []);
    if (!list.includes(index)) list.push(index);
  }

  /** Consumed spawn ordinals for `region`'s current visit. */
  getSpawnConsumed(region: string): ReadonlySet<number> {
    return new Set(this._spawnConsumed[region] ?? []);
  }

  getBag(): Readonly<Record<string, number>> {
    return { ...this._bag };
  }

  countItem(itemId: string): number {
    return this._bag[itemId] ?? 0;
  }

  hasItem(itemId: string): boolean {
    return this.countItem(itemId) > 0;
  }

  addItem(itemId: string, qty: number = 1): void {
    if (qty <= 0) return;
    this._bag[itemId] = (this._bag[itemId] ?? 0) + qty;
  }

  /** Remove `qty` of an item. Returns false (and removes nothing) when
   *  the bag holds fewer than `qty` — callers gate the use flow on the
   *  return value rather than pre-checking. Zero-count keys are deleted
   *  so getBag() never lists empties. */
  removeItem(itemId: string, qty: number = 1): boolean {
    if (qty <= 0) return false;
    const have = this._bag[itemId] ?? 0;
    if (have < qty) return false;
    if (have === qty) delete this._bag[itemId];
    else this._bag[itemId] = have - qty;
    return true;
  }

  /** Per-run drafted-species tracking. Used as the excluded-species set
   *  in rollOffers so the same species doesn't appear in two consecutive
   *  zone drafts. Resets on Reset Run via reset(). v7. */
  hasDraftedSpecies(species: string): boolean {
    return this._draftedSpeciesThisRun.has(species);
  }

  markSpeciesDrafted(species: string): void {
    this._draftedSpeciesThisRun.add(species);
  }

  getDraftedSpecies(): ReadonlySet<string> {
    return this._draftedSpeciesThisRun;
  }

  /** Pending-draft accessors. WorldScene sets this BEFORE launching
   *  DraftScene + saving; DraftScene clears it on pick. On reload with a
   *  non-null pendingDraft, WorldScene re-launches DraftScene with the
   *  stored offers (no re-roll). v7. */
  getPendingDraft(): PendingDraft | null {
    if (!this._pendingDraft) return null;
    return {
      zoneId: this._pendingDraft.zoneId,
      offers: this._pendingDraft.offers.map((o) => ({
        ...o,
        ivs: { ...o.ivs },
        moves: [...o.moves],
        types: [...o.types],
        caughtAt: { ...o.caughtAt },
      })),
    };
  }

  setPendingDraft(p: PendingDraft): void {
    this._pendingDraft = {
      zoneId: p.zoneId,
      offers: p.offers.map((o) => ({
        ...o,
        ivs: { ...o.ivs },
        moves: [...o.moves],
        types: [...o.types],
        caughtAt: { ...o.caughtAt },
      })),
    };
  }

  clearPendingDraft(): void {
    this._pendingDraft = null;
  }

  /** Pokédex accessors — THIS RUN tab data. Career entries (across-run)
   *  live in careerStore on a separate localStorage key (Step 7); this
   *  pokedex state clears on Reset Run + on each new run start. v7. */
  getPokedex(): PokedexThisRun {
    return {
      drafted: this._pokedex.drafted.map((d) => ({
        ...d,
        ivs: { ...d.ivs },
        moves: [...d.moves],
        movesDisplay: [...(d.movesDisplay ?? [])],
        types: [...d.types],
        baseStats: { ...(d.baseStats ?? { hp: 0, atk: 0, def: 0, spa: 0, spd: 0, spe: 0 }) },
        caughtAt: { ...d.caughtAt },
      })),
      skipped: this._pokedex.skipped.map((s) => ({
        ...s,
        offer: {
          ...s.offer,
          ivs: { ...s.offer.ivs },
          moves: [...s.offer.moves],
          movesDisplay: [...(s.offer.movesDisplay ?? [])],
          types: [...s.offer.types],
          baseStats: { ...(s.offer.baseStats ?? { hp: 0, atk: 0, def: 0, spa: 0, spd: 0, spe: 0 }) },
          caughtAt: { ...s.offer.caughtAt },
        },
      })),
      seenWild: { ...this._pokedex.seenWild },
    };
  }

  /** Append a drafted-Pokémon snapshot (immutable post-pick). DraftScene
   *  calls this at commit time with the fields copied from the picked
   *  DraftOffer. */
  appendDrafted(snapshot: PartyMemberSnapshot): void {
    this._pokedex.drafted.push({
      ...snapshot,
      ivs: { ...snapshot.ivs },
      moves: [...snapshot.moves],
      movesDisplay: [...snapshot.movesDisplay],
      types: [...snapshot.types],
      baseStats: { ...snapshot.baseStats },
      caughtAt: { ...snapshot.caughtAt },
    });
  }

  /** Append a skipped-offer entry. DraftScene calls this at commit time
   *  once per non-picked offer in the modal. */
  appendSkipped(entry: SkippedEntry): void {
    this._pokedex.skipped.push({
      ...entry,
      offer: {
        ...entry.offer,
        ivs: { ...entry.offer.ivs },
        moves: [...entry.offer.moves],
        movesDisplay: [...entry.offer.movesDisplay],
        types: [...entry.offer.types],
        baseStats: { ...entry.offer.baseStats },
        caughtAt: { ...entry.offer.caughtAt },
      },
    });
  }

  /** Tally a wild encounter. WorldScene calls this when a grass-step
   *  encounter triggers (the player has visually seen the species by
   *  that point). Updates count + level band; idempotent — re-calls for
   *  the same species merge into the existing entry. */
  recordSeenWild(species: string, level: number): void {
    const existing = this._pokedex.seenWild[species];
    if (existing) {
      existing.count += 1;
      if (level < existing.minLevel) existing.minLevel = level;
      if (level > existing.maxLevel) existing.maxLevel = level;
    } else {
      this._pokedex.seenWild[species] = {
        count: 1,
        minLevel: level,
        maxLevel: level,
      };
    }
  }

  /** Last-known overworld position. WorldScene writes this on every step
   *  + every map load; saveManager reads it through serialize() so the
   *  pagehide / visibilitychange flush captures the latest tile. Returns
   *  null when no run has started yet OR an old save migrated from v5. */
  getPosition(): PlayerPosition | null {
    return this._position ? { ...this._position } : null;
  }

  setPosition(p: PlayerPosition): void {
    this._position = { ...p };
  }

  serialize(): RunStateSnapshot {
    return {
      party: this._party.map((m) => ({
        ...m,
        hp: { ...m.hp },
        ivs: { ...m.ivs },
        caughtAt: { ...m.caughtAt },
      })),
      routesDrafted: [...this._routesDrafted],
      defeatedTrainers: [...this._defeatedTrainers],
      position: this._position ? { ...this._position } : null,
      draftedSpeciesThisRun: [...this._draftedSpeciesThisRun],
      pendingDraft: this._pendingDraft
        ? {
            zoneId: this._pendingDraft.zoneId,
            offers: this._pendingDraft.offers.map((o) => ({
              ...o,
              ivs: { ...o.ivs },
              moves: [...o.moves],
              types: [...o.types],
              caughtAt: { ...o.caughtAt },
            })),
          }
        : null,
      pokedex: this.getPokedex(),
      flags: [...this._flags],
      vars: { ...this._vars },
      bag: { ...this._bag },
      spawnVisits: { ...this._spawnVisits },
      spawnConsumed: Object.fromEntries(
        Object.entries(this._spawnConsumed).map(([k, v]) => [k, [...v]]),
      ),
    };
  }

  /** Restore from a snapshot. Expects the latest schema — migrations happen
   *  in saveManager before calling this. Defensive defaults on Step 2/3
   *  new fields keep cross-development saves loadable (a v7 save written
   *  mid-PR between steps would lack the newer fields; we don't ship that
   *  state but the safety net is cheap). */
  restore(snapshot: RunStateSnapshot): void {
    // Capture pre-restore ids so subscribers can see which slots
    // "disappeared" across the restore boundary. A restore that
    // replaces party [A, B, C] with [D, E] fires onMembersRemoved
    // for [A, B, C] (subscriber state-machines reset cleanly) and
    // then onPartyChanged (the new party is the fresh truth).
    const priorIds = this._party.map((m) => m.id);
    this._party = snapshot.party.map((m) => ({
      ...m,
      hp: { ...m.hp },
      nature: m.nature ?? 'hardy',
      ivs: m.ivs ? { ...m.ivs } : { ...ZERO_IVS },
      ability: m.ability ?? '',
      gender: m.gender ?? 'genderless',
      caughtAt: m.caughtAt ? { ...m.caughtAt } : { ...UNKNOWN_CAUGHT_AT },
      status: m.status ?? null,
    }));
    this._routesDrafted = new Set(snapshot.routesDrafted);
    this._defeatedTrainers = new Set(snapshot.defeatedTrainers ?? []);
    this._position = snapshot.position ? { ...snapshot.position } : null;
    this._draftedSpeciesThisRun = new Set(snapshot.draftedSpeciesThisRun ?? []);
    this._pendingDraft = snapshot.pendingDraft
      ? {
          zoneId: snapshot.pendingDraft.zoneId,
          offers: snapshot.pendingDraft.offers.map((o) => ({
            ...o,
            ivs: { ...o.ivs },
            moves: [...o.moves],
            types: [...o.types],
            gender: o.gender ?? 'genderless',
            caughtAt: o.caughtAt ? { ...o.caughtAt } : { ...UNKNOWN_CAUGHT_AT },
          })),
        }
      : null;
    const pdx = snapshot.pokedex;
    this._pokedex = pdx
      ? {
          drafted: pdx.drafted.map((d) => ({
            ...d,
            ivs: { ...d.ivs },
            moves: [...d.moves],
            types: [...d.types],
            gender: d.gender ?? 'genderless',
            caughtAt: d.caughtAt ? { ...d.caughtAt } : { ...UNKNOWN_CAUGHT_AT },
          })),
          skipped: pdx.skipped.map((s) => ({
            ...s,
            offer: {
              ...s.offer,
              ivs: { ...s.offer.ivs },
              moves: [...s.offer.moves],
              types: [...s.offer.types],
              gender: s.offer.gender ?? 'genderless',
              caughtAt: s.offer.caughtAt ? { ...s.offer.caughtAt } : { ...UNKNOWN_CAUGHT_AT },
            },
          })),
          seenWild: { ...pdx.seenWild },
        }
      : freshPokedex();
    // H1: defensive defaults so a snapshot written mid-PR (or a snapshot
    // from a hypothetical pre-v11 path) without these fields still loads.
    // Same shape as the defeatedTrainers / draftedSpeciesThisRun fallbacks
    // above. Test pinned in runState.test.ts.
    this._flags = new Set(snapshot.flags ?? []);
    this._vars = { ...(snapshot.vars ?? {}) };
    this._bag = { ...(snapshot.bag ?? {}) };
    this._spawnVisits = { ...(snapshot.spawnVisits ?? {}) };
    this._spawnConsumed = Object.fromEntries(
      Object.entries(snapshot.spawnConsumed ?? {}).map(([k, v]) => [k, [...v]]),
    );
    if (priorIds.length > 0) this.notifyMembersRemoved(priorIds);
    this.notifyPartyChanged();
  }

  reset(): void {
    const priorIds = this._party.map((m) => m.id);
    this._party = [];
    this._routesDrafted = new Set();
    this._defeatedTrainers = new Set();
    this._position = null;
    this._draftedSpeciesThisRun = new Set();
    this._pendingDraft = null;
    this._pokedex = freshPokedex();
    // PR 12 (H9): seed cartridge-default-SET FLAG_HIDE_* flags so the
    // pre-cinematic world looks right at run start (Oak hidden in lab,
    // rival hidden, item balls hidden, etc.). The 3 starter balls are
    // intentionally NOT in this list — they default-UNSET (visible),
    // DraftScene.commit sets them when the player picks any starter.
    this._flags = new Set(INITIAL_HIDDEN_FLAGS);
    this._vars = {};
    this._bag = {};
    this._spawnVisits = {};
    this._spawnConsumed = {};
    if (priorIds.length > 0) this.notifyMembersRemoved(priorIds);
    this.notifyPartyChanged();
  }
}

export const runState = new RunStateStore();
